import type { OrderDetailResponseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useOrderChargeListQuery } from "Order/Modules/Payment/Api/Query/useOrderChargeListQuery";
import { AddChargeForm } from "Order/Modules/Payment/Components/Forms/AddChargeForm";
import { EditChargeForm } from "Order/Modules/Payment/Components/Forms/EditChargeForm";
import { usePartnersListQuery } from "Partner/Api/Query/usePartnersListQuery";
import { transformAndSortPartners } from "Partner/Utilities/transformSortPartners";
import { Alert } from "Shared/Components/Alert";
import type { OptionType } from "Shared/Components/Form/CustomFormComponent/Select";
import { Loading } from "Shared/Components/Loading/Loading";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { useMemo } from "react";

type ChargeFormTabProps = {
	order: OrderDetailResponseDto;
};

const paymentResource = Resources.base.payment.shared;

export function ChargeFormTab({ order }: ChargeFormTabProps) {
	const { t } = useResource();
	const orderId = order.id;
	const {
		data: charges,
		isLoading,
		isFetching,
	} = useOrderChargeListQuery(orderId);
	const { data: partners } = usePartnersListQuery({
		marketId: order.market?.id,
		verticals: order.verticalId.toString(),
	});
	const partnerOptions = useMemo<OptionType[]>(() => {
		const options = transformAndSortPartners(
			partners?.data ?? [],
			order.region?.name,
		);

		if (
			order.partner &&
			!options.some((option) => option.value === order.partner?.id)
		) {
			options.unshift({
				value: order.partner.id,
				label: order.partner.fullName,
				group: order.region?.name ?? "Without region",
			});
		}

		charges?.forEach((charge) => {
			if (
				charge.partnerId != null &&
				!options.some((option) => option.value === charge.partnerId)
			) {
				options.push({
					value: charge.partnerId,
					label: `Partner #${charge.partnerId}`,
					group: "Previous partners",
				});
			}
		});

		return options;
	}, [charges, order.partner, order.region?.name, partners]);

	return (
		<Box px={3}>
			<Typography variant="h5">{t(paymentResource.addCharge)}</Typography>
			<AddChargeForm
				orderId={orderId}
				currentPartnerId={order.partner?.id ?? null}
				partnerOptions={partnerOptions}
			/>
			<Divider sx={{ my: 2 }} />
			<Typography variant="h5">{t(paymentResource.chargeDetails)}</Typography>

			{charges && charges?.length > 0 ? (
				<Stack spacing={3} mt={2}>
					{charges.map((charge) => (
						<EditChargeForm
							key={charge.id}
							charge={charge}
							orderId={orderId}
							currentPartnerId={order.partner?.id ?? null}
							partnerOptions={partnerOptions}
						/>
					))}
				</Stack>
			) : (
				!isLoading &&
				!isFetching && (
					<Alert
						message={"This order has no charges yet"}
						color="grey"
						sx={{ marginTop: 1 }}
					/>
				)
			)}
			{(isLoading || isFetching) && (
				<Stack width="100%" alignItems="center">
					<Loading />
				</Stack>
			)}
		</Box>
	);
}
